/**
 * Device cache of QR signing keys, the tenant's qr-signature-mode and
 * qr-binding-ticket-booking.
 *
 * A rep scans at a door with no signal, so the keys for the working week are
 * pulled ahead of time from GET /api/qr-keys and held in IndexedDB (database
 * `qr-verification`). The cache is merged by kid, never replaced: a `since`
 * delta does not re-send unchanged keys, including rotated-but-unexpired ones a
 * guest's offline phone is still signing with. Expired keys are dropped on load,
 * on every sync and on lookup.
 *
 * Refresh: on app start / login (once a week is selected), when the week
 * changes, when the device comes back online, when the app returns to the
 * foreground after REFRESH_MS, and every REFRESH_MS while open. The endpoint
 * gives no interval of its own; `truncated: true` means "come straight back",
 * which is honoured immediately. The server syncs from GBS every 20 minutes, so
 * polling faster than REFRESH_MS gains nothing.
 *
 * Key material never reaches a log, Sentry or an error message from here.
 * It is wiped on logout.
 */

import { openDB, deleteDB } from 'idb'
import { api } from '@/services/api.js'
import { DEFAULT_LEEWAY_SECONDS, normaliseQrMode, verifyScannedCode, QR_MODE } from '@/utils/qrVerify.js'
import { splitScannedCode } from '@/utils/scanUtils.js'

const DB_NAME = 'qr-verification'
const DB_VERSION = 1
const KEYS = 'keys'
const META = 'meta'

export const QR_MODE_FLAG = 'qr-signature-mode'
// Fallback binding: also accept the key of the booking on our cached ticket row.
export const QR_BINDING_FLAG = 'qr-binding-ticket-booking'
export const REFRESH_MS = 10 * 60 * 1000
/** A runaway guard on `truncated` follow-ups, not a page count anyone expects. */
const MAX_PAGES = 20

const nowSeconds = () => Math.floor(Date.now() / 1000)

let dbPromise = null
/** kid -> { kid, key, expiresAt, bookingReference, weekId } */
let memKeys = null
let loadPromise = null
let mode = null
let bindToTicketBooking = false
// reference (upper-cased) -> the booking on the screen's cached ticket row
let ticketBookings = new Map()
let leewaySeconds = DEFAULT_LEEWAY_SECONDS
let syncing = null
let timer = null
let lastRefreshAt = 0
let weekIdSource = null
let listenersBound = false

function getDB() {
  if (!dbPromise) {
    // Deferred so a missing indexedDB (private mode, some webviews) rejects
    // instead of throwing synchronously out of every caller.
    dbPromise = Promise.resolve().then(() => openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(KEYS)) db.createObjectStore(KEYS, { keyPath: 'kid' })
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: 'id' })
      },
    })).catch(err => {
      dbPromise = null
      throw err
    })
  }
  return dbPromise
}

/** Load the persisted cache into memory once, purging what has expired. */
function ensureLoaded() {
  if (memKeys) return Promise.resolve()
  if (!loadPromise) {
    loadPromise = (async () => {
      const map = new Map()
      try {
        const db = await getDB()
        const now = nowSeconds()
        const rows = await db.getAll(KEYS)
        const expired = []
        for (const row of rows) {
          if (Number(row.expiresAt) > now) map.set(row.kid, row)
          else expired.push(row.kid)
        }
        if (expired.length) {
          const tx = db.transaction(KEYS, 'readwrite')
          await Promise.all([...expired.map(kid => tx.store.delete(kid)), tx.done])
        }
        const modeRow = await db.get(META, 'mode')
        const bindingRow = await db.get(META, 'binding')
        if (bindingRow) bindToTicketBooking = bindingRow.enabled === true
        if (modeRow) mode = normaliseQrMode(modeRow.mode)
        const leewayRow = await db.get(META, 'leeway')
        if (leewayRow && Number.isFinite(Number(leewayRow.value))) leewaySeconds = Number(leewayRow.value)
      } catch {
        // No IndexedDB (private mode, corrupt store): verify from memory only.
      }
      memKeys = map
    })().finally(() => { loadPromise = null })
  }
  return loadPromise
}

/**
 * @param {string} kid
 * @returns {Promise<object|null>}
 */
export async function getQrKey(kid) {
  await ensureLoaded()
  const row = memKeys.get(kid)
  if (!row) return null
  if (Number(row.expiresAt) <= nowSeconds()) {
    memKeys.delete(kid)
    getDB().then(db => db.delete(KEYS, kid)).catch(() => {})
    return null
  }
  return row
}

/** The cached mode; 0 until the flag has been read at least once. */
export async function getQrMode() {
  await ensureLoaded()
  return mode ?? QR_MODE.OFF
}

/**
 * Verify a raw scan against the cache and the cached mode.
 * @returns {Promise<{raw, reference, token, verification, mode}>}
 */
export async function inspectScannedCode(raw) {
  const currentMode = await getQrMode()
  const booking = bindToTicketBooking ? ticketBookings.get(String(splitScannedCode(raw).reference || '').toUpperCase()) : null
  const result = await verifyScannedCode(raw, {
    getKey: getQrKey,
    mode: currentMode,
    leewaySeconds,
    bookingRefs: booking ? [booking] : [],
  })
  return { ...result, mode: currentMode }
}

/** Read qr-signature-mode for this build's tenant. Keeps the cached value on failure. */
export async function refreshQrMode() {
  try {
    const res = await api.getFlag(QR_MODE_FLAG)
    // Off or missing flag means off; a number flag carries its value in `value`.
    const next = res && res.enabled ? normaliseQrMode(res.value) : QR_MODE.OFF
    mode = next
    try {
      const db = await getDB()
      await db.put(META, { id: 'mode', mode: next, fetchedAt: Date.now() })
    } catch { /* memory only */ }
  } catch (err) {
    // 404 is "flag not registered yet" - off. Anything else keeps what we had.
    if (err?.status === 404) mode = QR_MODE.OFF
  }
  return mode ?? QR_MODE.OFF
}

/**
 * Pull keys for a week and merge them in by kid.
 * @param {string|number} weekId the week index the rest of the PWA uses
 */
export async function syncQrKeys(weekId) {
  if (!weekId) return
  if (syncing) return syncing
  syncing = (async () => {
    await ensureLoaded()
    const db = await getDB().catch(() => null)
    const cursorId = `cursor:${weekId}`
    let since = null
    try {
      since = db ? (await db.get(META, cursorId))?.syncedAt ?? null : null
    } catch { since = null }

    for (let page = 0; page < MAX_PAGES; page++) {
      const res = await api.getQrKeys(weekId, since)
      const now = nowSeconds()
      const keys = Array.isArray(res?.keys) ? res.keys : []
      const fresh = []
      for (const k of keys) {
        if (!k || typeof k.kid !== 'string' || typeof k.key !== 'string') continue
        if (!(Number(k.expiresAt) > now)) continue
        fresh.push({
          kid: k.kid,
          key: k.key,
          expiresAt: Number(k.expiresAt),
          bookingReference: k.bookingReference ?? null,
          weekId: String(weekId),
        })
      }
      for (const row of fresh) memKeys.set(row.kid, row)

      if (Number.isFinite(Number(res?.leewaySeconds))) leewaySeconds = Number(res.leewaySeconds)

      if (db) {
        try {
          const tx = db.transaction([KEYS, META], 'readwrite')
          const ops = fresh.map(row => tx.objectStore(KEYS).put(row))
          if (Number.isFinite(Number(res?.syncedAt))) {
            ops.push(tx.objectStore(META).put({ id: cursorId, syncedAt: Number(res.syncedAt) }))
          }
          if (Number.isFinite(Number(res?.leewaySeconds))) {
            ops.push(tx.objectStore(META).put({ id: 'leeway', value: Number(res.leewaySeconds) }))
          }
          await Promise.all([...ops, tx.done])
        } catch { /* memory only this session */ }
      }

      if (!res?.truncated || !Number.isFinite(Number(res?.syncedAt))) break
      since = Number(res.syncedAt)
    }
    await purgeExpired()
  })().finally(() => { syncing = null })
  return syncing
}

async function purgeExpired() {
  const now = nowSeconds()
  const expired = []
  for (const [kid, row] of memKeys) {
    if (Number(row.expiresAt) <= now) expired.push(kid)
  }
  if (!expired.length) return
  expired.forEach(kid => memKeys.delete(kid))
  try {
    const db = await getDB()
    const tx = db.transaction(KEYS, 'readwrite')
    await Promise.all([...expired.map(kid => tx.store.delete(kid)), tx.done])
  } catch { /* next load purges */ }
}

/**
 * The tickets the current screen holds, so a scan of one can bind to its
 * booking when qr-binding-ticket-booking is on. Replaces the previous list.
 * @param {Array<{barcode?: string, bookingRef?: string}>} tickets
 */
export function rememberTicketBookings(tickets) {
  ticketBookings = new Map()
  for (const ticket of tickets || []) {
    if (ticket?.barcode && ticket?.bookingRef) ticketBookings.set(String(ticket.barcode).toUpperCase(), String(ticket.bookingRef))
  }
}

/** Read qr-binding-ticket-booking for this build's tenant. Keeps the cached value on failure. */
export async function refreshQrBinding() {
  try {
    const res = await api.getFlag(QR_BINDING_FLAG)
    bindToTicketBooking = res?.enabled === true
    try {
      const db = await getDB()
      await db.put(META, { id: 'binding', enabled: bindToTicketBooking, fetchedAt: Date.now() })
    } catch { /* memory only */ }
  } catch (err) {
    if (err?.status === 404) bindToTicketBooking = false
  }
  return bindToTicketBooking
}

/** Mode and keys together. Never throws; failures leave the cache as it was. */
export async function refreshQrVerification(weekId = weekIdSource?.()) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  lastRefreshAt = Date.now()
  await refreshQrMode()
  await refreshQrBinding()
  // /api/qr-keys needs a session; without one api.request would sign the app out.
  if (!weekId || !api.refreshToken) return
  try {
    await syncQrKeys(weekId)
  } catch (err) {
    // Status only: the body of a failed key response is not something to print.
    console.warn('[qr] key sync failed', err?.status ?? '')
  }
}

const onOnline = () => { refreshQrVerification() }
const onVisible = () => {
  if (typeof document !== 'undefined' && !document.hidden && Date.now() - lastRefreshAt >= REFRESH_MS) {
    refreshQrVerification()
  }
}

/**
 * Start the refresh loop. Idempotent.
 * @param {() => (string|number|null)} getWeekId current week index
 */
export function startQrKeyRefresh(getWeekId) {
  weekIdSource = getWeekId
  if (!listenersBound && typeof window !== 'undefined') {
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    listenersBound = true
  }
  if (!timer) timer = setInterval(() => { refreshQrVerification() }, REFRESH_MS)
  refreshQrVerification()
}

export function stopQrKeyRefresh() {
  if (timer) clearInterval(timer)
  timer = null
  if (listenersBound && typeof window !== 'undefined') {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisible)
    listenersBound = false
  }
}

/** Wipe every key and cursor. Called on logout. The mode is not secret and is kept. */
export async function clearQrKeyCache() {
  memKeys = new Map()
  try {
    const db = await getDB()
    const tx = db.transaction(KEYS, 'readwrite')
    await Promise.all([tx.store.clear(), tx.done])
    const cursors = (await db.getAllKeys(META)).filter(id => String(id).startsWith('cursor:'))
    const tx2 = db.transaction(META, 'readwrite')
    await Promise.all([...cursors.map(id => tx2.store.delete(id)), tx2.done])
  } catch {
    try { await deleteDB(DB_NAME) } catch { /* nothing left to do */ }
    dbPromise = null
  }
}

/** Tests only. */
export function __resetQrKeyCacheForTests() {
  memKeys = null
  loadPromise = null
  mode = null
  bindToTicketBooking = false
  ticketBookings = new Map()
  leewaySeconds = DEFAULT_LEEWAY_SECONDS
  syncing = null
  dbPromise = null
}
